import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { tripService } from '../../services/trip.service';
import { invoiceService } from '../../services/invoice.service';
import { fuelService } from '../../services/fuel.service';
import { allowanceService } from '../../services/allowance.service';
import { payrollService } from '../../services/payroll.service';
import { AllowanceStatus, Trip, Invoice, FuelRecord, Allowance, PayrollRecord, PayrollStatus } from '../../types';

export const ReportsPage: React.FC = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const { data: trips, isLoading: loadingTrips, error: tripsError } = useQuery({
    queryKey: ['reports', 'trips', startDate, endDate],
    queryFn: () => tripService.getByDateRange(startDate, endDate),
    enabled: Boolean(startDate) && Boolean(endDate),
  });

  const { data: invoices, isLoading: loadingInvoices, error: invoicesError } = useQuery({
    queryKey: ['reports', 'invoices', startDate, endDate],
    queryFn: () => invoiceService.getByDateRange(startDate, endDate),
    enabled: Boolean(startDate) && Boolean(endDate),
  });

  const { data: flaggedFuel, isLoading: loadingFuel, error: fuelError } = useQuery({
    queryKey: ['reports', 'fuel', 'flagged'],
    queryFn: fuelService.getFlagged,
  });

  const { data: pendingAllowances, isLoading: loadingAllowances, error: allowancesError } = useQuery({
    queryKey: ['reports', 'allowances', 'pending'],
    queryFn: () => allowanceService.getByStatus(AllowanceStatus.PENDING),
  });

  // Get current year and month for payroll
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const { data: payrollRecords, isLoading: loadingPayroll, error: payrollError } = useQuery({
    queryKey: ['reports', 'payroll', currentYear],
    queryFn: () => payrollService.getByYear(currentYear),
  });

  const { data: currentMonthPayroll, isLoading: loadingCurrentPayroll } = useQuery({
    queryKey: ['reports', 'payroll', currentYear, currentMonth],
    queryFn: () => payrollService.getByPeriod(currentYear, currentMonth),
  });

  const tripsList = useMemo<Trip[]>(() => trips || [], [trips]);
  const invoicesList = useMemo<Invoice[]>(() => invoices || [], [invoices]);
  const flaggedFuelList = useMemo<FuelRecord[]>(() => flaggedFuel || [], [flaggedFuel]);
  const pendingAllowancesList = useMemo<Allowance[]>(() => pendingAllowances || [], [pendingAllowances]);
  const payrollList = useMemo<PayrollRecord[]>(() => payrollRecords || [], [payrollRecords]);
  const currentMonthPayrollList = useMemo<PayrollRecord[]>(() => currentMonthPayroll || [], [currentMonthPayroll]);

  const totalInvoiceAmount = useMemo(() => {
    return invoicesList.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
  }, [invoicesList]);

  // Payroll statistics
  const payrollStats = useMemo(() => {
    const totalGrossPay = payrollList.reduce((sum, p) => sum + (p.grossPay || 0), 0);
    const totalNetPay = payrollList.reduce((sum, p) => sum + (p.netPay || 0), 0);
    const pendingPayroll = payrollList.filter(p => p.status === PayrollStatus.PENDING_APPROVAL || p.status === PayrollStatus.DRAFT);
    const paidPayroll = payrollList.filter(p => p.status === PayrollStatus.PAID);
    const currentMonthTotal = currentMonthPayrollList.reduce((sum, p) => sum + (p.netPay || 0), 0);
    
    return {
      totalGrossPay,
      totalNetPay,
      pendingCount: pendingPayroll.length,
      paidCount: paidPayroll.length,
      totalRecords: payrollList.length,
      currentMonthTotal,
      currentMonthCount: currentMonthPayrollList.length,
    };
  }, [payrollList, currentMonthPayrollList]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', maximumFractionDigits: 0 }).format(value);
  };

  const getPayrollStatusVariant = (status: PayrollStatus) => {
    switch (status) {
      case PayrollStatus.PAID: return 'success';
      case PayrollStatus.APPROVED: return 'info';
      case PayrollStatus.PENDING_APPROVAL: return 'warning';
      case PayrollStatus.DRAFT: return 'default';
      case PayrollStatus.CANCELLED: return 'error';
      default: return 'default';
    }
  };

  const anyLoading = loadingTrips || loadingInvoices || loadingFuel || loadingAllowances || loadingPayroll || loadingCurrentPayroll;
  const anyError = tripsError || invoicesError || fuelError || allowancesError || payrollError;

  return (
    <div className="space-y-6">
      <Card
        title="Reports"
        action={
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="w-40">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="w-40">
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        }
      >
        {anyLoading && <div className="text-sm text-gray-600">Loading report data...</div>}
        {!anyLoading && anyError && <div className="text-sm text-error">Failed to load report data.</div>}

        {!anyLoading && !anyError && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-neutral-light rounded-lg p-4">
                <div className="text-sm text-gray-600">Trips (range)</div>
                <div className="text-2xl font-semibold text-neutral-charcoal">{tripsList.length}</div>
              </div>
              <div className="bg-neutral-light rounded-lg p-4">
                <div className="text-sm text-gray-600">Invoices (range)</div>
                <div className="text-2xl font-semibold text-neutral-charcoal">{invoicesList.length}</div>
              </div>
              <div className="bg-neutral-light rounded-lg p-4">
                <div className="text-sm text-gray-600">Invoice Total (range)</div>
                <div className="text-2xl font-semibold text-neutral-charcoal">{formatCurrency(totalInvoiceAmount)}</div>
              </div>
              <div className="bg-neutral-light rounded-lg p-4">
                <div className="text-sm text-gray-600">Flagged Fuel</div>
                <div className="text-2xl font-semibold text-neutral-charcoal">{flaggedFuelList.length}</div>
              </div>
            </div>

            {/* Payroll Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-700">Payroll This Month</div>
                <div className="text-2xl font-semibold text-green-800">{formatCurrency(payrollStats.currentMonthTotal)}</div>
                <div className="text-xs text-green-600 mt-1">{payrollStats.currentMonthCount} employee(s)</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-700">Total Net Pay ({currentYear})</div>
                <div className="text-2xl font-semibold text-blue-800">{formatCurrency(payrollStats.totalNetPay)}</div>
                <div className="text-xs text-blue-600 mt-1">{payrollStats.totalRecords} record(s)</div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="text-sm text-yellow-700">Pending Payroll</div>
                <div className="text-2xl font-semibold text-yellow-800">{payrollStats.pendingCount}</div>
                <div className="text-xs text-yellow-600 mt-1">Awaiting approval</div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-sm text-purple-700">Paid Payroll ({currentYear})</div>
                <div className="text-2xl font-semibold text-purple-800">{payrollStats.paidCount}</div>
                <div className="text-xs text-purple-600 mt-1">Completed payments</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title="Recent Invoices">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Trip</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoicesList.slice(0, 5).length === 0 ? (
                      <TableRow>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={4}>
                          No invoices in range.
                        </td>
                      </TableRow>
                    ) : (
                      invoicesList.slice(0, 5).map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell className="font-medium">{inv.invoiceNumber}</TableCell>
                          <TableCell>{inv.tripNumber}</TableCell>
                          <TableCell>{formatCurrency(inv.totalAmount)}</TableCell>
                          <TableCell>
                            <Badge variant="info">{inv.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>

              <Card title="Pending Allowances">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trip</TableHead>
                      <TableHead>Driver</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingAllowancesList.slice(0, 5).length === 0 ? (
                      <TableRow>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={5}>
                          No pending allowances.
                        </td>
                      </TableRow>
                    ) : (
                      pendingAllowancesList.slice(0, 5).map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium">{a.tripNumber}</TableCell>
                          <TableCell>{a.driverName}</TableCell>
                          <TableCell>{String(a.type)}</TableCell>
                          <TableCell>{formatCurrency(a.amount)}</TableCell>
                          <TableCell>
                            <Badge variant="warning">{a.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>

            {/* Payroll Records */}
            <Card title={`Payroll Records - ${currentYear}`}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payroll #</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Basic Salary</TableHead>
                    <TableHead>Gross Pay</TableHead>
                    <TableHead>Net Pay</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollList.length === 0 ? (
                    <TableRow>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={7}>
                        No payroll records for {currentYear}.
                      </td>
                    </TableRow>
                  ) : (
                    payrollList.slice(0, 10).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.payrollNumber}</TableCell>
                        <TableCell>{p.userName}</TableCell>
                        <TableCell>{p.payrollPeriod}</TableCell>
                        <TableCell>{formatCurrency(p.basicSalary)}</TableCell>
                        <TableCell>{formatCurrency(p.grossPay)}</TableCell>
                        <TableCell className="font-semibold">{formatCurrency(p.netPay)}</TableCell>
                        <TableCell>
                          <Badge variant={getPayrollStatusVariant(p.status)}>{p.status.replace('_', ' ')}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {payrollList.length > 10 && (
                <div className="text-center py-2 text-sm text-gray-500">
                  Showing 10 of {payrollList.length} records. <a href="/payroll" className="text-primary hover:underline">View all</a>
                </div>
              )}
            </Card>

            <Card title="Flagged Fuel Records">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Trip</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Variance</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flaggedFuelList.slice(0, 10).length === 0 ? (
                    <TableRow>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={5}>
                        No flagged fuel records.
                      </td>
                    </TableRow>
                  ) : (
                    flaggedFuelList.slice(0, 10).map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          {r.fuelDate ? format(parseISO(r.fuelDate), 'MMM dd, yyyy') : '-'}
                        </TableCell>
                        <TableCell className="font-medium">{r.tripNumber}</TableCell>
                        <TableCell>{r.vehicleRegistration}</TableCell>
                        <TableCell>
                          {typeof r.variancePercentage === 'number'
                            ? `${r.variancePercentage.toFixed(2)}%`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="warning">Flagged</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}
      </Card>
    </div>
  );
};
